import { api } from './client'

// ── Shapes (see backend Features/Sites) ──────────────────────────────────────

export type SiteStatus = 'Draft' | 'Live' | 'Offline' | 'Suspended'

export type WebsiteStatus = {
  sitesEnabled: boolean
  hasSite: boolean
  status: SiteStatus | null
  canEdit: boolean
  primaryHostname: string | null
  publishedAt: string | null
  hasUnpublishedChanges: boolean
  newEnquiries: number
}

export type SiteSummary = {
  id: string
  name: string
  status: SiteStatus
  templateKey: string
  themeJson: string
  settingsJson: string
  revision: number
  hasUnpublishedChanges: boolean
  publishedAt: string | null
  publishedVersion: number | null
  customerEditingEnabled: boolean
  enquiryRetentionDays: number
}

export type SitePage = {
  id: string
  title: string
  slug: string
  kind: 'System' | 'Custom'
  systemKey: string | null
  isPublished: boolean
  showInNav: boolean
  navLabel: string | null
  navOrder: number
  showInFooter: boolean
  footerOrder: number
  seoTitle: string | null
  seoDescription: string | null
  socialImageId: string | null
  noIndex: boolean
  blocksJson: string
  updatedAt: string
}

export type SiteDomain = { id: string; hostname: string; kind: string; status: string; isPrimary: boolean }
export type SiteTemplate = { key: string; name: string; description: string | null; previewImageUrl: string | null; optionsSchemaJson: string }

export type SiteFont = { key: string; label: string; group: string; family: string }

export type BlockFieldKind =
  | 'Text' | 'Multiline' | 'Markdown' | 'Link' | 'Image' | 'Choice' | 'Bool' | 'Int' | 'Guid' | 'GuidList' | 'Email' | 'Items'

export type BlockField = {
  name: string
  kind: BlockFieldKind
  required: boolean
  max: number
  choices: string[] | null
  min: number
  maxValue: number
  sub: BlockField[] | null
}

export type BlockType = { type: string; version: number; label: string; fields: BlockField[] }

export type SiteEditor = {
  site: SiteSummary
  pages: SitePage[]
  domains: SiteDomain[]
  templates: SiteTemplate[]
  blockTypes: BlockType[]
  blockOptions: BlockField[]
  fonts: SiteFont[]
  /** Where the template showroom lives (previews of each template). */
  demoUrl: string
  /** Whether stock photo search is set up (a Pixabay key is configured). */
  stockPictures: boolean
}

export type SiteVersion = { versionNumber: number; publishedAt: string; publishedByEmail: string; note: string | null; isRollback: boolean; isLive: boolean }
export type SitePicture = {
  id: string; altText: string; focusX: number; focusY: number; width: number; height: number; sizeBytes: number; fileName: string | null
  /** Page titles (and "Site settings") that use this picture. */
  usedOn: string[]
}
export type StockPhoto = { id: string; previewUrl: string; width: number; height: number; photographer: string; pageUrl: string; description: string }
export type StockSearchResult = { photos: StockPhoto[]; total: number }

export type SiteEnquiry = {
  id: string; receivedAt: string; pageSlug: string | null; name: string; email: string; phone: string | null; message: string
  status: 'New' | 'Read' | 'Archived' | 'Spam'; deliveryStatus: 'Pending' | 'Sent' | 'Failed'
}
export type SiteEnquiryPage = { items: SiteEnquiry[]; total: number; newCount: number }

export type PageSave = {
  expectedRevision: number
  title: string
  slug: string | null
  isPublished: boolean
  showInNav: boolean
  navLabel: string | null
  showInFooter: boolean
  seoTitle: string | null
  seoDescription: string | null
  socialImageId: string | null
  noIndex: boolean
  blocksJson: string
}

export type SettingsSave = {
  expectedRevision: number
  name?: string | null
  templateKey?: string | null
  themeJson?: string | null
  settingsJson?: string | null
}

export type CatalogueOptions = {
  courses: { id: string; name: string; categoryId: string | null; showOnWebsite: boolean }[]
  categories: { id: string; name: string }[]
}

export type SiteOverviewRow = {
  tenantId: string; tenantName: string; tenantSlug: string; sitesEnabled: boolean; hasSite: boolean; status: SiteStatus | null
  customerEditingEnabled: boolean; publishedAt: string | null; primaryHostname: string | null; newEnquiries: number
}

// ── Client ───────────────────────────────────────────────────────────────────

/**
 * The website editor's endpoints. A tenant admin uses their own (`/website`); CertSlate staff use the same set for a chosen tenant
 * (`/superadmin/sites/{tenantId}`), so the editor itself doesn't care which it is talking to.
 */
export function websiteApi(base: string) {
  return {
    base,
    getStatus: () => api.get<WebsiteStatus>(`${base}/status`).then(r => r.data),
    getCatalogue: () => api.get<CatalogueOptions>(`${base}/catalogue`).then(r => r.data),
    getEditor: () => api.get<SiteEditor>(`${base}/editor`).then(r => r.data),
    saveSettings: (data: SettingsSave) => api.put<{ revision: number }>(`${base}/settings`, data).then(r => r.data.revision),
    createPage: (data: { expectedRevision: number; title: string; slug: string | null }) =>
      api.post<{ id: string }>(`${base}/pages`, data).then(r => r.data.id),
    updatePage: (id: string, data: PageSave) => api.put<{ revision: number }>(`${base}/pages/${id}`, data).then(r => r.data.revision),
    deletePage: (id: string, expectedRevision: number) =>
      api.delete<{ revision: number }>(`${base}/pages/${id}`, { params: { expectedRevision } }).then(r => r.data.revision),
    reorderPages: (expectedRevision: number, order: { pageId: string; navOrder: number; footerOrder: number }[]) =>
      api.put<{ revision: number }>(`${base}/pages/order`, { expectedRevision, order }).then(r => r.data.revision),
    publish: (expectedRevision: number, note: string | null) =>
      api.post<{ version: number }>(`${base}/publish`, { expectedRevision, note }).then(r => r.data.version),
    getVersions: () => api.get<SiteVersion[]>(`${base}/versions`).then(r => r.data),
    rollback: (versionNumber: number, note?: string) =>
      api.post<{ version: number }>(`${base}/versions/${versionNumber}/rollback`, { note: note ?? null }).then(r => r.data.version),
    setOnline: (online: boolean) => api.put(`${base}/online`, { online }),
    createPreviewLink: (expiresInHours = 72) =>
      api.post<{ url: string; expiresAt: string }>(`${base}/preview-links`, { expiresInHours }).then(r => r.data),
    revokePreviewLinks: () => api.delete(`${base}/preview-links`),
    // usedOn is missing from answers given by an API that hasn't been updated yet, so never rely on it being there.
    getPictures: () => api.get<SitePicture[]>(`${base}/pictures`).then(r => r.data.map(p => ({ ...p, usedOn: p.usedOn ?? [] }))),
    uploadPicture: (file: File, altText: string) => {
      const form = new FormData()
      form.append('file', file)
      form.append('altText', altText)
      return api.post<SitePicture>(`${base}/pictures`, form).then(r => r.data)
    },
    updatePicture: (id: string, data: { altText: string; focusX: number; focusY: number }) => api.put(`${base}/pictures/${id}`, data),
    deletePicture: (id: string) => api.delete(`${base}/pictures/${id}`),
    searchStock: (q: string, page: number) =>
      api.get<StockSearchResult>(`${base}/stock-pictures`, { params: { q, page } }).then(r => r.data),
    importStock: (id: string, altText: string) => api.post<SitePicture>(`${base}/stock-pictures`, { id, altText }).then(r => r.data),
    getEnquiries: (status?: string, page = 1) =>
      api.get<SiteEnquiryPage>(`${base}/enquiries`, { params: { status, page } }).then(r => r.data),
    setEnquiryStatus: (id: string, status: string) => api.put(`${base}/enquiries/${id}/status`, { status }),
    deleteEnquiry: (id: string) => api.delete(`${base}/enquiries/${id}`),
  }
}

export type WebsiteApi = ReturnType<typeof websiteApi>

/** The caller's own organisation's website. */
export const ownWebsiteApi = websiteApi('/website')

/** CertSlate staff acting on one organisation's website, plus the controls only staff have. */
export function staffWebsiteApi(tenantId: string) {
  const base = `/superadmin/sites/${tenantId}`
  return {
    ...websiteApi(base),
    setSitesEnabled: (enabled: boolean) => api.put(`${base}/sites-enabled`, { enabled }),
    createSite: () => api.post<{ id: string }>(`${base}/create`).then(r => r.data.id),
    setCustomerEditing: (enabled: boolean) => api.put(`${base}/customer-editing`, { enabled }),
    setSuspended: (suspended: boolean, reason?: string) => api.put(`${base}/suspended`, { suspended, reason: reason ?? null }),
  }
}

export const websiteOverviewApi = {
  list: () => api.get<SiteOverviewRow[]>('/superadmin/sites').then(r => r.data),
}

/** A picture on the website, through the public address (so it is the same one the live site uses). */
export function sitePictureUrl(id: string, width?: number) {
  return `${api.defaults.baseURL}/public/website-images/${id}${width ? `?w=${width}` : ''}`
}

/** The message from a failed request, as the server worded it. */
export function websiteError(e: unknown): { message: string; conflict: boolean; validation: boolean } {
  const err = e as { response?: { status?: number; data?: { detail?: string; title?: string } } }
  const status = err?.response?.status
  const data = err?.response?.data
  return {
    message: data?.detail ?? data?.title ?? 'Something went wrong. Please try again.',
    conflict: status === 409,
    validation: status === 400,
  }
}
